import React, { useMemo } from 'react';
import Badge from 'components/ui/Badge';
import { ActivityType } from 'interfaces/common.interface';
import { HABIT_CHOICE_TYPE, PRIORITY_OPTION } from 'constants/general';
import { t } from 'services/i18n.service';

interface HabitFeatureTagsProps {
  activity: ActivityType;
  className?: string;
}

interface BadgeConfig {
  icon: string;
  text: string;
  borderColor: string;
  bgColor: string;
  textColor: string;
  condition: boolean;
}

const HabitFeatureTags: React.FC<HabitFeatureTagsProps> = ({
  activity,
  className = ''
}) => {
  const badgeConfigs: BadgeConfig[] = useMemo(
    () => [
      {
        condition: !!(activity?.video_urls ?? []).length,
        icon: '📽️',
        text: t('activity_info.url'),
        borderColor: 'border-red-400',
        bgColor: 'bg-red-50 dark:bg-red-900/20',
        textColor: 'text-red-700 dark:text-red-300'
      },
      {
        condition: Boolean(activity?.log_quantity),
        icon: '✍️',
        text: t('activity_info.log_data'),
        borderColor: 'border-orange-400',
        bgColor: 'bg-orange-50 dark:bg-orange-900/20',
        textColor: 'text-orange-700 dark:text-orange-300'
      },
      {
        condition:
          !!(activity?.allowed_apps ?? []).length ||
          !!(activity?.allowed_urls ?? []).length ||
          Boolean(activity?.allowed_focus_mode_id),
        icon: '💻',
        text: t('activity_info.allowed_action'),
        borderColor: 'border-blue-400',
        bgColor: 'bg-blue-50 dark:bg-blue-900/20',
        textColor: 'text-blue-700 dark:text-blue-300'
      },
      {
        condition: activity?.choice_type === HABIT_CHOICE_TYPE.RANDOM,
        icon: '🎲',
        text: t('activity_info.choice'),
        borderColor: 'border-blue-400',
        bgColor: 'bg-blue-50 dark:bg-blue-900/20',
        textColor: 'text-blue-700 dark:text-blue-300'
      },
      {
        condition: activity?.priority === PRIORITY_OPTION.HIGH,
        icon: '⬆️',
        text: t('activity_info.high_priority_activity'),
        borderColor: 'border-green-400',
        bgColor: 'bg-green-50 dark:bg-green-900/20',
        textColor: 'text-green-700 dark:text-green-300'
      },
      {
        condition: Boolean(activity?.include_in_every_break),
        icon: '♾️',
        text: t('activity_info.include_in_every_break'),
        borderColor: 'border-yellow-400',
        bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
        textColor: 'text-yellow-700 dark:text-yellow-300'
      }
    ],
    [activity]
  );

  const activeBadges = badgeConfigs.filter((config) => config.condition);

  if (activeBadges.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-wrap items-center ~gap-2/3 ${className}`}>
      {activeBadges.map((badge, index) => (
        <div key={`${badge.icon}-${index}`} className='group'>
          <Badge
            variant='outline'
            size='sm'
            className={`${badge.borderColor} ${badge.bgColor} ${badge.textColor} overflow-hidden transition-all duration-300 ease-in-out w-auto min-w-fit !~p-0.5/1`}>
            <span className='~text-xs/sm'>{badge.icon}</span>
            <span className='max-w-0 opacity-0 h-0 overflow-hidden whitespace-normal transition-opacity ease-in-out group-hover:max-w-[50vw] md:group-hover:max-w-full group-hover:opacity-100 group-hover:h-auto group-hover:ml-1 ~text-[0.6rem]/xs'>
              {badge.text}
            </span>
          </Badge>
        </div>
      ))}
    </div>
  );
};

export default HabitFeatureTags;
